import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight, Globe, X } from 'lucide-react';
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown
} from 'reactstrap'

const languages = [
    { label: 'English', value: '/es/en', id: 'en' },
    { label: 'Español', value: '/es/es', id: 'es' },
    { label: 'Français', value: '/es/fr', id: 'fr' },
    { label: 'Deutsch', value: '/es/de', id: 'gr' },
    { label: 'Italiano', value: '/es/it', id: 'it' },
    { label: 'Português', value: '/es/pt', id: 'pt' },
    { label: 'Polski', value: '/es/pl', id: 'pl' },
    { label: 'Türkçe', value: '/es/tr', id: 'tr' },
    { label: 'Čeština', value: '/es/cs', id: 'cz' },
    { label: 'Slovenčina', value: '/es/sk', id: 'sk' },
    { label: 'Català', value: '/es/ca', id: 'cat' },
    { label: 'Nederlands', value: '/es/nl', id: 'nl' },
    { label: 'Norsk', value: '/es/no', id: 'nw' }
];

// ── Smartphone picker ─────────────────────────────────────────────────────────
// Mobile menus render <GoogleTranslate variant="modal" />: a pill with the current
// flag + language that opens a bottom-sheet modal with big, tappable language
// tiles (the desktop dropdown list was cramped and clipped inside the mobile menu).
const FLAGS_PATH = '/languages';
const LNGM_CSS = `
.lngm-trigger { display: inline-flex; align-items: center; gap: 8px; max-width: 60vw; padding: 5px 10px 5px 5px; border-radius: 999px; border: 1px solid var(--topbar-btn-border, rgba(0,0,0,.1)); background: transparent; color: var(--sidebar-text, #111827); font-size: 14px; font-weight: 500; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: transform .15s ease; }
.lngm-trigger:active { transform: scale(.97); }
.lngm-trigger-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lngm-trigger-flag { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; box-shadow: 0 1px 3px rgba(0,0,0,.2); flex-shrink: 0; }
.lngm-trigger-chevron { opacity: .6; flex-shrink: 0; }
.lngm-overlay { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: flex-end; justify-content: center; background: rgba(15,23,42,.55); animation: lngm-fade .18s ease-out; }
.lngm-sheet { width: 100%; max-width: 520px; max-height: 85vh; overflow-y: auto; padding: 8px 16px calc(16px + env(safe-area-inset-bottom)); border-radius: 20px 20px 0 0; background: var(--sidebar-bg, #ffffff); color: var(--sidebar-text, #111827); box-shadow: 0 -8px 30px rgba(0,0,0,.25); animation: lngm-up .22s cubic-bezier(.2,.8,.2,1); }
.lngm-grabber { width: 40px; height: 4px; margin: 4px auto 10px; border-radius: 999px; background: currentColor; opacity: .2; }
.lngm-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.lngm-title { display: inline-flex; align-items: center; gap: 8px; font-size: 17px; font-weight: 600; }
.lngm-close { width: 36px; height: 36px; border: none; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: var(--sidebar-btn-hover, rgba(0,0,0,.05)); color: inherit; cursor: pointer; }
.lngm-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.lngm-option { display: flex; align-items: center; gap: 10px; min-height: 52px; padding: 10px 12px; border-radius: 14px; border: 1.5px solid var(--sidebar-border, rgba(0,0,0,.08)); background: transparent; color: inherit; font-size: 15px; text-align: left; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: background .15s ease, border-color .15s ease, transform .15s ease; }
.lngm-option:active { transform: scale(.98); }
.lngm-option--active { border-color: #3b82f6; background: rgba(59,130,246,.12); }
.lngm-option-flag { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; box-shadow: 0 1px 3px rgba(0,0,0,.2); flex-shrink: 0; }
.lngm-option-label { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lngm-option-check { color: #3b82f6; flex-shrink: 0; }
@keyframes lngm-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes lngm-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) { .lngm-overlay, .lngm-sheet { animation: none; } }
`;

function LanguageModalPicker({ currentLang, onSelect }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (document.getElementById('lngm-styles')) return;
        const style = document.createElement('style');
        style.id = 'lngm-styles';
        style.textContent = LNGM_CSS;
        document.head.appendChild(style);
    }, []);

    // While open: lock page scroll and close with Escape
    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document.body.style.overflow;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const current = currentLang || languages.find(lang => lang.id === 'es');

    return (
        <>
            <button type="button" className="lngm-trigger" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label="Seleccionar idioma">
                <img src={`${FLAGS_PATH}/${current.id}.png`} alt="" className="lngm-trigger-flag" />
                <span className="lngm-trigger-label notranslate" translate="no">{current.label}</span>
                <ChevronRight size={16} className="lngm-trigger-chevron" />
            </button>
            {open && createPortal(
                <div className="lngm-overlay" onClick={() => setOpen(false)}>
                    <div className="lngm-sheet" role="dialog" aria-modal="true" aria-label="Seleccionar idioma" onClick={(e) => e.stopPropagation()}>
                        <div className="lngm-grabber" />
                        <div className="lngm-header">
                            <span className="lngm-title"><Globe size={20} /> Idioma</span>
                            <button type="button" className="lngm-close" onClick={() => setOpen(false)} aria-label="Cerrar">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="lngm-grid">
                            {languages.map((lang) => {
                                const active = lang.value === current.value;
                                return (
                                    <button
                                        key={lang.value}
                                        type="button"
                                        className={`lngm-option${active ? ' lngm-option--active' : ''}`}
                                        onClick={() => { setOpen(false); if (!active) onSelect(lang.value); }}
                                        aria-pressed={active}
                                    >
                                        <img src={`${FLAGS_PATH}/${lang.id}.png`} alt="" className="lngm-option-flag" />
                                        <span className="lngm-option-label notranslate" translate="no">{lang.label}</span>
                                        {active && <Check size={16} className="lngm-option-check" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

export default function GoogleTranslate({ variant } = {}) {
    const [selected, setSelected] = useState('/es/es');

    useEffect(() => {
        // Set dropdown to match active cookie
        const cookieMatch = document.cookie.match(/googtrans=([^;]+)/);
        setSelected(cookieMatch ? cookieMatch[1] : '/es/es');

        // Avoid injecting script or re-initializing if already done
        if (window._googleTranslateInitialized) return;
        window._googleTranslateInitialized = true;

        // Cleanup conflicting cookies BEFORE script loads
        document.cookie = `googtrans=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

        // Init Google Translate callback
        window.googleTranslateElementInit = () => {
            new window.google.translate.TranslateElement(
                {
                    pageLanguage: 'es',
                    autoDisplay: false,
                    includedLanguages: languages.map(l => l.value.split('/')[2]).join(','),
                    layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
                },
                'google_translate_element'
            );
        };

        // Inject Google Translate script only once
        if (!document.querySelector('script[src*="translate.google.com"]')) {
            const script = document.createElement('script');
            script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            document.body.appendChild(script);
        }
    }, []);

    const handleLanguageClick = (langValue) => {
        const targetLang = langValue.split('/')[2];
        const sourceLang = 'es';
        const googtransValue = `/${sourceLang}/${targetLang}`;

        document.cookie = `googtrans=${googtransValue}; path=/; domain=.vacway.es`;
        document.cookie = `googtrans=${googtransValue}; path=/; domain=${window.location.hostname}`;
        document.cookie = `googtrans=${googtransValue}; path=/`;

        setSelected(googtransValue);
        
        // Delay reload slightly to let React finish cleanup
        setTimeout(() => {
            window.location.reload();
        }, 10);
    };

    const currentLang = languages.find(lang => lang.value === selected);

    // Smartphone menu: modal picker instead of the dropdown list
    if (variant === 'modal') {
        return <LanguageModalPicker currentLang={currentLang} onSelect={handleLanguageClick} />;
    }

    return (
        <>
            <div id="google_translate_element" className="d-none" />
            <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret className="lang-toggle" title="Idioma" aria-label="Seleccionar idioma">
                    <img
                        src={`/languages/${currentLang?.id || 'es'}.png`}
                        alt={currentLang?.label || 'Español'}
                        className="lang-flag"
                    />
                </DropdownToggle>
                <DropdownMenu right>
                    {languages.map((lang) => (
                        <DropdownItem
                            key={lang.value}
                            onClick={() => handleLanguageClick(lang.value)}
                            className="d-flex align-items-center notranslate cursor-pointer"
                            translate="no"
                        >
                            <img
                                src={`/languages/${lang.id}.png`}
                                alt={lang.label}
                                className="mr-2"
                                style={{ width: 25, height: 25 }}
                            />
                            {lang.label}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </UncontrolledDropdown>
        </>
    );
}
